import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormHelper } from '@helpers/form.helper';
import { enumEntityDataSource } from '@data-layer/enums/isf/enumEntityDataSource';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { TranslateService } from '@ngx-translate/core';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { IsfStateService } from '../../services';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';

@Component({
  selector: 'entity-unify-card',
  templateUrl: './entity-unify-card.component.html',
  styleUrls: ['./entity-unify-card.component.scss'],
})
export class EntityUnifyCardComponent implements OnInit, OnDestroy {
  @Input() public isfEntity: EntityModel;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public code: enumEntityModelCode = null;
  @Input() public index: number;
  @Input() public isCreating = false;
  @Input() public isDesktop = false;
  @Input() public tooltipData?: string;
  @Output() public areaClicked: EventEmitter<void> = new EventEmitter<void>();

  public label: string;
  public dataSourceControl: AbstractControl;
  public enumEntityDataSource = enumEntityDataSource;
  public isShowIdentifierBlock = true;
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;

  private enumIdentityQual = enumIdentifierQualifier;
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private fh: FormHelper,
    private router: Router,
    private translateService: TranslateService,
    private isfStateService: IsfStateService,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.isShowIdentifierBlock = !this.checkShowIdentifierBlock();

    this.label = `INFO.${this.code.toLocaleUpperCase()}.USE_${this.isfEntity?.entityCode?.toLocaleUpperCase()}${
      this.isDesktop ? '_DESKTOP' : ''
    }`;
    if ((!!this.index || this.index === 0) && this.isfEntity.entityCode === enumEntityModelCode.MANUFACTURER) {
      const mfList = this.isfStateService.getIsfManufacturerEntityList();
      const mfIndex = (mfList || []).findIndex((item) => item.id === this.isfEntity?.id) + 1;

      this.label = this.translateService.instant(
        `INFO.${this.code.toLocaleUpperCase()}.USE_${this.isfEntity?.entityCode?.toLocaleUpperCase()}${this.isDesktop ? '_DESKTOP' : ''}`,
        { index: mfIndex },
      );
    }
    this.dataSourceControl = this.formGroup.get('dataSource');

    this.dataSourceControl.valueChanges.pipe(takeUntil(this.isDestroy)).subscribe((value) => {
      this.updateFormVal(value);
    });
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public editUP() {
    const returnUrl: string = this.router.url;
    const company: CompanyModel = this.companiesService.getCompany();
    this.router.navigate(['/companies', 'account-settings', 'company-view', company?.id], { queryParams: { returnUrl } });
  }

  public get isShowCard(): boolean {
    return this.isfEntity.id === this.dataSourceControl?.value && !this.isDesktop;
  }

  private checkShowIdentifierBlock(): boolean {
    const isHideV =
      [
        enumEntityModelCode.SHIP_TO,
        enumEntityModelCode.MANUFACTURER,
        enumEntityModelCode.STAFFING_LOCATION,
        enumEntityModelCode.CONSOLIDATOR,
      ].indexOf(this.code) !== -1;

    return (
      (this.isfEntity.entityIdentifierQualifier === this.enumIdentityQual.EIN ||
        this.isfEntity.entityIdentifierQualifier === this.enumIdentityQual.SSN) &&
      isHideV
    );
  }

  private updateFormVal(value: string, skipId?: boolean): void {
    if (this.isfEntity.id === Number(value)) {
      const identifierRequired = [enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER].includes(
        this.code,
      );
      this.fh.patchIsfEntity(this.formGroup, { ...this.isfEntity, id: skipId ? null : this.isfEntity.id }, { emitEvent: false });
      if (!!this.isfEntity.address) {
        this.fh.patchAddress(this.formGroup.get('address') as UntypedFormGroup, this.isfEntity?.address, true, { emitEvent: false });
      }
      this.fh.updateValidatorsByEntityIdentifierQual(this.formGroup, identifierRequired, this.isfEntity.entityIdentifierQualifier, {
        emitEvent: false,
      });
    }
  }
}
